// Phase 21 · Floating reactions bar (5 emoji + animated overlay)
import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import useEventPolling from "../../hooks/useEventPolling";

const API = process.env.REACT_APP_BACKEND_URL;
const EMOJIS = ["👍", "❤️", "😂", "🎉", "🔥"];

export default function ReactionsBar({ roomId, canReact = true }) {
    const [floats, setFloats] = useState([]);
    const seenRef = useRef(new Set());

    // Poll recent reactions every 3s (with 10s window)
    const { data } = useEventPolling(
        roomId ? `${API}/api/community/rooms/${roomId}/reactions/recent?since_seconds=10` : null,
        { intervalMs: 3000 }
    );

    useEffect(() => {
        const items = data?.items || [];
        const now = Date.now();
        items.forEach((r) => {
            if (seenRef.current.has(r.id)) return;
            seenRef.current.add(r.id);
            const id = `${r.id}-${now}`;
            setFloats((f) => [...f, { id, emoji: r.emoji, x: 30 + Math.random() * 40 }]);
            setTimeout(() => {
                setFloats((f) => f.filter((it) => it.id !== id));
            }, 3500);
        });
        // Clear the seen-set periodically to avoid unbounded growth
        if (seenRef.current.size > 500) {
            seenRef.current = new Set(items.map((r) => r.id));
        }
    }, [data]);

    const send = async (emoji) => {
        if (!canReact) { toast.error("Join the room to react"); return; }
        try {
            const r = await fetch(`${API}/api/community/rooms/${roomId}/reactions`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ emoji }),
            });
            if (!r.ok) {
                if (r.status === 429) toast.error("Slow down");
                else toast.error("Reaction failed");
                return;
            }
            // Local optimistic float
            const id = `local-${Date.now()}-${Math.random()}`;
            setFloats((f) => [...f, { id, emoji, x: 50 }]);
            setTimeout(() => setFloats((f) => f.filter((it) => it.id !== id)), 3500);
        } catch { /* ignore */ }
    };

    return (
        <div className="relative" data-testid="reactions-bar">
            {/* Floating overlay */}
            <div className="pointer-events-none absolute inset-x-0 bottom-full h-[200px] overflow-hidden">
                <AnimatePresence>
                    {floats.map((f) => (
                        <motion.div
                            key={f.id}
                            initial={{ opacity: 0, y: 40, scale: 0.6 }}
                            animate={{ opacity: [0, 1, 1, 0], y: -180, scale: 1.2 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 3.5, ease: "easeOut" }}
                            style={{ left: `${f.x}%` }}
                            className="absolute bottom-0 text-3xl"
                        >
                            {f.emoji}
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
            <div className="flex items-center justify-center gap-2 py-2">
                {EMOJIS.map((e) => (
                    <button
                        key={e}
                        type="button"
                        onClick={() => send(e)}
                        disabled={!canReact}
                        className={`w-10 h-10 rounded-full text-xl transition-all ${canReact ? "bg-[#111111] border border-[#1A1A1A] hover:border-[#FFFFFF]/40 hover:scale-110" : "opacity-40 cursor-not-allowed"}`}
                        data-testid={`react-${e}`}
                    >
                        {e}
                    </button>
                ))}
            </div>
        </div>
    );
}
